import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { api, fetchImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, Vote, ScrollText, Trash2, Plus, Save,
  UserCheck, UserX, Search, Zap, Newspaper, Clock, Feather, IdCard, XCircle,
} from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function Avatar({ name, picture, size = "h-11 w-11", text = "text-xl" }) {
  if (picture) {
    return <img src={picture} alt={name} className={`${size} rounded-full border border-gold/40`} referrerPolicy="no-referrer" />;
  }
  return (
    <span className={`flex ${size} items-center justify-center rounded-full bg-navy font-amiri ${text} text-gold`}>
      {name?.[0] || "ن"}
    </span>
  );
}

function VerificationDialog({ userId, name }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  const openDialog = async () => {
    setOpen(true);
    setFailed(false);
    setUrl(null);
    try {
      setUrl(await fetchImageUrl(`/admin/verifications/image/${userId}`));
    } catch {
      setFailed(true);
    }
  };

  const closeDialog = (o) => {
    setOpen(o);
    if (!o && url) {
      URL.revokeObjectURL(url);
      setUrl(null);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={openDialog}
        data-testid={`pending-view-id-${userId}`}
        className="rounded-full border-gold/50 px-5 font-bold text-navy hover:bg-gold/10"
      >
        <IdCard className="ml-1.5 h-4 w-4" /> عرض الهوية
      </Button>
      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent className="max-w-2xl" dir="rtl" data-testid={`id-dialog-${userId}`}>
          <DialogHeader>
            <DialogTitle className="font-amiri text-2xl text-navy">الهوية الجامعية — {name}</DialogTitle>
          </DialogHeader>
          {url ? (
            <img src={url} alt={`هوية ${name}`} className="max-h-[70vh] w-full rounded-xl border border-gold/25 object-contain" />
          ) : failed ? (
            <p className="py-10 text-center text-sm text-red-500">تعذر تحميل الصورة</p>
          ) : (
            <p className="py-10 text-center text-sm text-muted-foreground">جارٍ تحميل الصورة...</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Overview({ stats, pendingCount, onGoPending, onGoVotes, onGoBylaws, onActivateAll, activatingAll }) {
  if (!stats) return <p className="py-16 text-center text-muted-foreground">جارٍ التحميل...</p>;
  const cards = [
    { label: "إجمالي الأعضاء", value: stats.members, testid: "stat-members", icon: Users },
    { label: "أعضاء مفعَّلون", value: stats.active, testid: "stat-active", icon: UserCheck },
    { label: "المنشورات", value: stats.posts, testid: "stat-posts", icon: Newspaper },
    { label: "التصويتات", value: stats.votes, testid: "stat-votes", icon: Vote },
  ];
  return (
    <div className="space-y-4" data-testid="admin-stats-grid">
      <motion.button
        onClick={onGoPending}
        data-testid="stat-pending-card"
        className="flex w-full flex-wrap items-center gap-4 rounded-2xl border border-amber-500/40 bg-amber-50 p-6 text-right transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_36px_rgba(217,119,6,0.15)]"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500 text-white">
          <Clock className="h-7 w-7" />
        </span>
        <span className="flex-1">
          <span className="block font-amiri text-4xl font-bold text-amber-600" data-testid="stat-pending">{pendingCount}</span>
          <span className="text-sm font-medium text-amber-700">حساب بانتظار التفعيل — اضغط للمراجعة</span>
        </span>
        {pendingCount > 0 && (
          <Button
            onClick={(e) => { e.stopPropagation(); onActivateAll(); }}
            disabled={activatingAll}
            data-testid="activate-all-button"
            className="rounded-full bg-gold px-6 font-bold text-navy transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
          >
            <Zap className="ml-2 h-4 w-4" />
            {activatingAll ? "جارٍ التفعيل..." : "تفعيل الجميع"}
          </Button>
        )}
      </motion.button>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            className="rounded-2xl border border-gold/25 bg-white p-6 shadow-sm"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 + i * 0.07, ease: EASE }}
          >
            <c.icon className="mb-3 h-5 w-5 text-gold-dark" />
            <p className="font-amiri text-4xl font-bold text-gold-gradient" data-testid={c.testid}>{c.value}</p>
            <p className="mt-1 text-sm text-muted-foreground">{c.label}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="grid grid-cols-1 gap-4 rounded-2xl border border-gold/25 bg-white p-6 shadow-sm sm:grid-cols-2"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4, ease: EASE }}
        data-testid="quick-actions-card"
      >
        <p className="flex items-center gap-2 font-amiri text-xl text-navy sm:col-span-2">
          <Zap className="h-5 w-5 text-gold-dark" /> إجراءات سريعة
        </p>
        <Button onClick={onGoVotes} variant="outline" data-testid="quick-new-vote" className="justify-start rounded-xl border-gold/40 py-6 font-bold text-navy hover:bg-gold/10">
          <Vote className="ml-2 h-5 w-5 text-gold-dark" /> نشر تصويت جديد
        </Button>
        <Button onClick={onGoBylaws} variant="outline" data-testid="quick-edit-bylaws" className="justify-start rounded-xl border-gold/40 py-6 font-bold text-navy hover:bg-gold/10">
          <ScrollText className="ml-2 h-5 w-5 text-gold-dark" /> تحرير النظام الداخلي
        </Button>
      </motion.div>
    </div>
  );
}

function PendingMembers({ members, verifMap, onActivate, onDelete, onActivateAll, activatingAll, onRejectId }) {
  if (!members) return <p className="py-16 text-center text-muted-foreground">جارٍ التحميل...</p>;
  const pending = members.filter((m) => !m.is_active);

  if (pending.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gold/40 py-20 text-center" data-testid="pending-empty">
        <UserCheck className="mx-auto mb-4 h-10 w-10 text-gold/50" />
        <p className="font-amiri text-2xl text-muted-foreground">لا توجد حسابات بانتظار التفعيل</p>
        <p className="mt-2 text-sm text-muted-foreground">كل من سجّل دخوله قد فُعِّل حسابه.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pending-members-list">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/40 bg-amber-50 p-5">
        <p className="flex items-center gap-2 text-sm font-bold text-amber-700">
          <Clock className="h-5 w-5" />
          {pending.length} حساب سجّل دخوله وينتظر التفعيل
        </p>
        <Button
          onClick={onActivateAll}
          disabled={activatingAll}
          data-testid="activate-all-button-pending"
          className="rounded-full bg-gold px-6 font-bold text-navy transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
        >
          <Zap className="ml-2 h-4 w-4" />
          {activatingAll ? "جارٍ التفعيل..." : "تفعيل الجميع"}
        </Button>
      </div>

      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <IdCard className="h-4 w-4 text-gold-dark" />
        راجع الهوية الجامعية قبل التفعيل للتأكد أن صاحب الحساب طالب فعلاً.
      </p>

      {pending.map((m, i) => {
        const ver = verifMap?.[m.user_id];
        return (
          <motion.div
            key={m.user_id}
            className="rounded-2xl border border-gold/25 bg-white p-5 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: Math.min(i * 0.06, 0.4), ease: EASE }}
            data-testid={`pending-row-${m.user_id}`}
          >
            <div className="flex flex-wrap items-center gap-4">
              <Avatar name={m.name} picture={m.picture} />
              <div className="min-w-0 flex-1">
                <p className="font-bold text-navy">{m.name}</p>
                <p className="truncate text-xs text-muted-foreground" dir="ltr">{m.email}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  سجّل في {m.created_at ? format(new Date(m.created_at), "d MMMM yyyy", { locale: ar }) : "—"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => onActivate(m.user_id, false)}
                  data-testid={`pending-activate-${m.user_id}`}
                  className="rounded-full bg-gold px-5 font-bold text-navy transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
                >
                  <UserCheck className="ml-1.5 h-4 w-4" /> تفعيل
                </Button>
                <Button
                  onClick={() => onActivate(m.user_id, true)}
                  variant="outline"
                  data-testid={`pending-activate-post-${m.user_id}`}
                  className="rounded-full border-gold/50 px-5 font-bold text-navy hover:bg-gold/10"
                  title="تفعيل الحساب ومنحه إذن النشر"
                >
                  <Feather className="ml-1.5 h-4 w-4" /> تفعيل + إذن نشر
                </Button>
                <button
                  onClick={() => onDelete(m.user_id, m.name)}
                  data-testid={`pending-delete-${m.user_id}`}
                  className="rounded-full p-2.5 text-muted-foreground transition-[color,background-color] duration-300 hover:bg-red-50 hover:text-red-500"
                  title="حذف الحساب"
                >
                  <UserX className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gold/15 pt-4" data-testid={`pending-verification-${m.user_id}`}>
              {ver?.status === "pending" && (
                <>
                  <span className="flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 px-3 py-1 text-xs font-bold text-gold-dark">
                    <IdCard className="h-3.5 w-3.5" /> أرفق هويته الجامعية
                  </span>
                  <VerificationDialog userId={m.user_id} name={m.name} />
                  <button
                    onClick={() => onRejectId(m.user_id)}
                    data-testid={`pending-reject-id-${m.user_id}`}
                    className="flex items-center gap-1.5 rounded-full border border-red-400/40 bg-red-50 px-3 py-1 text-xs font-bold text-red-600 transition-colors hover:bg-red-100"
                  >
                    <XCircle className="h-3.5 w-3.5" /> رفض الهوية
                  </button>
                </>
              )}
              {ver?.status === "rejected" && (
                <span className="flex items-center gap-1.5 rounded-full border border-red-400/40 bg-red-50 px-3 py-1 text-xs font-bold text-red-600">
                  <XCircle className="h-3.5 w-3.5" /> هويته مرفوضة — بانتظار إعادة الرفع
                </span>
              )}
              {!ver && (
                <span className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500">
                  <IdCard className="h-3.5 w-3.5" /> لم يرفع الهوية الجامعية بعد
                </span>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function Members({ members, onUpdate, onDelete }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!members) return null;
    const q = query.trim().toLowerCase();
    const list = q
      ? members.filter((m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
      : members;
    return [...list].sort((a, b) => Number(a.is_active) - Number(b.is_active));
  }, [members, query]);

  if (!filtered) return <p className="py-16 text-center text-muted-foreground">جارٍ التحميل...</p>;

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ابحث بالاسم أو البريد الإلكتروني..."
          data-testid="member-search-input"
          className="border-input bg-white py-6 pr-11 text-navy placeholder:text-muted-foreground focus-visible:ring-gold"
        />
      </div>

      {filtered.length === 0 && (
        <p className="rounded-2xl border border-dashed border-gold/40 py-12 text-center text-muted-foreground" data-testid="members-no-results">
          لا توجد نتائج مطابقة
        </p>
      )}

      <div className="space-y-4" data-testid="admin-members-list">
        {filtered.map((m) => (
          <div
            key={m.user_id}
            className="flex flex-wrap items-center gap-4 rounded-2xl border border-gold/25 bg-white p-5 shadow-sm"
            data-testid={`member-row-${m.user_id}`}
          >
            <Avatar name={m.name} picture={m.picture} />
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 font-bold text-navy">
                {m.name}
                {m.role === "admin" && (
                  <span className="rounded-full border border-gold/50 bg-gold/10 px-2 py-0.5 text-[10px] font-bold text-gold-dark">أدمن</span>
                )}
                {!m.is_active && (
                  <span className="rounded-full border border-amber-500/50 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600">بانتظار التفعيل</span>
                )}
              </p>
              <p className="truncate text-xs text-muted-foreground" dir="ltr">{m.email}</p>
            </div>
            <div className="flex flex-wrap items-center gap-5">
              <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                تفعيل الحساب
                <Switch
                  checked={m.is_active}
                  disabled={m.role === "admin"}
                  onCheckedChange={(v) => onUpdate(m.user_id, { is_active: v })}
                  data-testid={`member-active-switch-${m.user_id}`}
                />
              </label>
              <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                إذن النشر
                <Switch
                  checked={m.can_post}
                  disabled={m.role === "admin"}
                  onCheckedChange={(v) => onUpdate(m.user_id, { can_post: v })}
                  data-testid={`member-canpost-switch-${m.user_id}`}
                />
              </label>
              {m.role !== "admin" && (
                <button
                  onClick={() => onDelete(m.user_id, m.name)}
                  data-testid={`member-delete-${m.user_id}`}
                  className="rounded-full p-2 text-muted-foreground transition-[color,background-color] duration-300 hover:bg-red-50 hover:text-red-500"
                  title="حذف الحساب"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PostsManager({ posts, setPosts }) {
  if (!posts) return <p className="py-16 text-center text-muted-foreground">جارٍ التحميل...</p>;

  const remove = async (postId) => {
    if (!window.confirm("حذف هذا المنشور نهائياً؟")) return;
    try {
      await api(`/posts/${postId}`, { method: "DELETE" });
      setPosts((p) => p.filter((x) => x.post_id !== postId));
      toast.success("تم حذف المنشور");
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (posts.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gold/40 py-20 text-center">
        <Newspaper className="mx-auto mb-4 h-10 w-10 text-gold/50" />
        <p className="font-amiri text-2xl text-muted-foreground">لا توجد منشورات بعد</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-posts-list">
      {posts.map((p) => (
        <div key={p.post_id} className="rounded-2xl border border-gold/25 bg-white p-5 shadow-sm" data-testid={`admin-post-row-${p.post_id}`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Avatar name={p.author_name} picture={p.author_picture} size="h-9 w-9" text="text-base" />
              <div>
                <p className="text-sm font-bold text-navy">{p.author_name}</p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(p.created_at), "d MMMM yyyy — h:mm a", { locale: ar })}
                </p>
              </div>
            </div>
            <button
              onClick={() => remove(p.post_id)}
              data-testid={`admin-post-delete-${p.post_id}`}
              className="rounded-full p-2 text-muted-foreground transition-[color,background-color] duration-300 hover:bg-red-50 hover:text-red-500"
              title="حذف المنشور"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <p className="whitespace-pre-wrap text-sm leading-7 text-navy/90">{p.body}</p>
        </div>
      ))}
    </div>
  );
}

function VotesManager({ votes, setVotes }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      await api("/votes", { method: "POST", body: JSON.stringify({ title: title.trim(), description: description.trim() }) });
      toast.success("تم إنشاء التصويت");
      setTitle("");
      setDescription("");
      const fresh = await api("/votes");
      setVotes(fresh);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (voteId, isOpen) => {
    try {
      await api(`/votes/${voteId}`, { method: "PATCH", body: JSON.stringify({ is_open: isOpen }) });
      setVotes((v) => v.map((x) => (x.vote_id === voteId ? { ...x, is_open: isOpen } : x)));
      toast.success(isOpen ? "تم فتح التصويت" : "تم إغلاق التصويت");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (voteId) => {
    if (!window.confirm("حذف هذا التصويت وكل أصواته نهائياً؟")) return;
    try {
      await api(`/votes/${voteId}`, { method: "DELETE" });
      setVotes((v) => v.filter((x) => x.vote_id !== voteId));
      toast.success("تم حذف التصويت");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gold/25 bg-white p-6 shadow-sm" data-testid="admin-vote-form">
        <h3 className="mb-4 flex items-center gap-2 font-amiri text-2xl text-navy">
          <Plus className="h-5 w-5 text-gold-dark" /> تصويت جديد
        </h3>
        <div className="space-y-3">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="موضوع التصويت"
            maxLength={200}
            data-testid="admin-vote-title-input"
            className="border-input bg-slate-50 text-navy placeholder:text-muted-foreground focus-visible:ring-gold"
          />
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="وصف التصويت (اختياري)"
            maxLength={2000}
            data-testid="admin-vote-description-input"
            className="min-h-[100px] border-input bg-slate-50 text-navy placeholder:text-muted-foreground focus-visible:ring-gold"
          />
          <Button
            onClick={create}
            disabled={creating || !title.trim()}
            data-testid="admin-vote-create-button"
            className="rounded-full bg-gold px-8 font-bold text-navy transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-50"
          >
            {creating ? "جارٍ الإنشاء..." : "نشر التصويت"}
          </Button>
        </div>
      </div>

      <div className="space-y-4" data-testid="admin-votes-list">
        {(votes || []).map((v) => (
          <div key={v.vote_id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-gold/25 bg-white p-5 shadow-sm" data-testid={`admin-vote-row-${v.vote_id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-navy">{v.title}</p>
              <p className="text-xs text-muted-foreground">
                {v.total} صوت — نعم {v.counts?.yes || 0} / لا {v.counts?.no || 0} / امتناع {v.counts?.abstain || 0}
              </p>
            </div>
            <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              {v.is_open ? "مفتوح" : "مغلق"}
              <Switch
                checked={v.is_open}
                onCheckedChange={(val) => toggle(v.vote_id, val)}
                data-testid={`admin-vote-toggle-${v.vote_id}`}
              />
            </label>
            <button
              onClick={() => remove(v.vote_id)}
              data-testid={`admin-vote-delete-${v.vote_id}`}
              className="rounded-full p-2 text-muted-foreground transition-[color,background-color] duration-300 hover:bg-red-50 hover:text-red-500"
              title="حذف التصويت"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {votes && votes.length === 0 && (
          <p className="rounded-2xl border border-dashed border-gold/40 py-12 text-center text-muted-foreground">لا توجد تصويتات بعد</p>
        )}
      </div>
    </div>
  );
}

function BylawsEditor({ bylaws, setBylaws }) {
  const [title, setTitle] = useState(null);
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (bylaws && title === null) {
      setTitle(bylaws.title);
      setContent(bylaws.content);
    }
  }, [bylaws, title]);

  if (!bylaws || title === null) return <p className="py-16 text-center text-muted-foreground">جارٍ التحميل...</p>;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api("/bylaws", { method: "PUT", body: JSON.stringify({ title: title.trim(), content: content.trim() }) });
      setBylaws(updated);
      toast.success("تم حفظ النظام الداخلي");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gold/25 bg-white p-6 shadow-sm" data-testid="admin-bylaws-editor">
      <h3 className="mb-4 flex items-center gap-2 font-amiri text-2xl text-navy">
        <ScrollText className="h-5 w-5 text-gold-dark" /> تحرير النظام الداخلي
      </h3>
      <div className="space-y-3">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="عنوان الوثيقة"
          maxLength={200}
          data-testid="admin-bylaws-title-input"
          className="border-input bg-slate-50 text-navy focus-visible:ring-gold"
        />
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="مواد النظام الداخلي..."
          data-testid="admin-bylaws-content-input"
          className="min-h-[420px] border-input bg-slate-50 font-amiri text-lg leading-9 text-navy focus-visible:ring-gold"
        />
        <Button
          onClick={save}
          disabled={saving || !title.trim() || !content.trim()}
          data-testid="admin-bylaws-save-button"
          className="rounded-full bg-gold px-8 font-bold text-navy transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-50"
        >
          <Save className="ml-2 h-4 w-4" />
          {saving ? "جارٍ الحفظ..." : "حفظ ونشر"}
        </Button>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState(null);
  const [votes, setVotes] = useState(null);
  const [bylaws, setBylaws] = useState(null);
  const [posts, setPosts] = useState(null);
  const [verifs, setVerifs] = useState(null);
  const [activatingAll, setActivatingAll] = useState(false);

  const loadStats = () => api("/admin/stats").then(setStats).catch((e) => toast.error(e.message));
  const loadMembers = () => api("/admin/members").then(setMembers).catch((e) => toast.error(e.message));
  const loadVerifs = () => api("/admin/verifications").then(setVerifs).catch((e) => toast.error(e.message));

  useEffect(() => {
    if (user.role !== "admin") return;
    loadStats();
    loadMembers();
    loadVerifs();
    api("/votes").then(setVotes).catch((e) => toast.error(e.message));
    api("/bylaws").then(setBylaws).catch((e) => toast.error(e.message));
    api("/posts").then(setPosts).catch((e) => toast.error(e.message));
  }, [user.role]);

  const pendingCount = members ? members.filter((m) => !m.is_active).length : (stats?.pending ?? 0);
  const verifMap = useMemo(
    () => (verifs ? Object.fromEntries(verifs.map((v) => [v.user_id, v])) : {}),
    [verifs]
  );

  if (user.role !== "admin") return <Navigate to="/posts" replace />;

  const updateMember = async (userId, patch) => {
    try {
      const updated = await api(`/admin/members/${userId}`, { method: "PATCH", body: JSON.stringify(patch) });
      setMembers((m) => m.map((x) => (x.user_id === userId ? updated : x)));
      toast.success("تم تحديث العضو");
      loadStats();
      loadVerifs();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const activateMember = (userId, withPostPermission) => {
    updateMember(userId, withPostPermission ? { is_active: true, can_post: true } : { is_active: true });
  };

  const deleteMember = async (userId, name) => {
    if (!window.confirm(`حذف حساب «${name}» نهائياً؟ سيُحذف معه سجل تصويتاته وهويته المرفوعة.`)) return;
    try {
      await api(`/admin/members/${userId}`, { method: "DELETE" });
      setMembers((m) => m.filter((x) => x.user_id !== userId));
      toast.success("تم حذف الحساب");
      loadStats();
      loadVerifs();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const rejectId = async (userId) => {
    try {
      await api(`/admin/verifications/${userId}/reject`, { method: "POST" });
      toast.success("تم رفض الهوية — سيُطلب من العضو إعادة الرفع");
      loadVerifs();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const activateAll = async () => {
    setActivatingAll(true);
    try {
      const res = await api("/admin/members/activate-all", { method: "POST" });
      toast.success(`تم تفعيل ${res.activated} حساب`);
      await loadMembers();
      loadStats();
      loadVerifs();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActivatingAll(false);
    }
  };

  return (
    <div data-testid="admin-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
        <h1 className="mb-2 flex items-center gap-3 font-amiri text-4xl text-navy sm:text-5xl">
          <LayoutDashboard className="h-8 w-8 text-gold-dark" />
          لوحة التحكم
        </h1>
        <p className="mb-8 text-muted-foreground">إدارة الأعضاء والتحقق الطلابي والمنشورات والتصويتات والنظام الداخلي.</p>
      </motion.div>

      <Tabs value={tab} onValueChange={setTab} dir="rtl" data-testid="admin-tabs">
        <TabsList className="mb-8 flex h-auto w-full flex-wrap justify-start gap-1 rounded-2xl border border-gold/25 bg-white p-1.5 shadow-sm">
          <TabsTrigger value="overview" data-testid="admin-tab-overview" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-gold data-[state=active]:text-navy">
            نظرة عامة
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="admin-tab-pending" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-gold data-[state=active]:text-navy">
            <Clock className="ml-1.5 h-4 w-4" />
            بانتظار التفعيل
            {pendingCount > 0 && (
              <span className="mr-1.5 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="pending-count-badge">
                {pendingCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="admin-tab-members" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-gold data-[state=active]:text-navy">
            <Users className="ml-1.5 h-4 w-4" />
            الأعضاء
          </TabsTrigger>
          <TabsTrigger value="posts" data-testid="admin-tab-posts" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-gold data-[state=active]:text-navy">
            <Newspaper className="ml-1.5 h-4 w-4" />
            المنشورات
          </TabsTrigger>
          <TabsTrigger value="votes" data-testid="admin-tab-votes" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-gold data-[state=active]:text-navy">
            <Vote className="ml-1.5 h-4 w-4" />
            التصويتات
          </TabsTrigger>
          <TabsTrigger value="bylaws" data-testid="admin-tab-bylaws" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-gold data-[state=active]:text-navy">
            <ScrollText className="ml-1.5 h-4 w-4" />
            النظام الداخلي
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Overview
            stats={stats}
            pendingCount={pendingCount}
            onGoPending={() => setTab("pending")}
            onGoVotes={() => setTab("votes")}
            onGoBylaws={() => setTab("bylaws")}
            onActivateAll={activateAll}
            activatingAll={activatingAll}
          />
        </TabsContent>
        <TabsContent value="pending">
          <PendingMembers
            members={members}
            verifMap={verifMap}
            onActivate={activateMember}
            onDelete={deleteMember}
            onActivateAll={activateAll}
            activatingAll={activatingAll}
            onRejectId={rejectId}
          />
        </TabsContent>
        <TabsContent value="members"><Members members={members} onUpdate={updateMember} onDelete={deleteMember} /></TabsContent>
        <TabsContent value="posts"><PostsManager posts={posts} setPosts={setPosts} /></TabsContent>
        <TabsContent value="votes"><VotesManager votes={votes} setVotes={setVotes} /></TabsContent>
        <TabsContent value="bylaws"><BylawsEditor bylaws={bylaws} setBylaws={setBylaws} /></TabsContent>
      </Tabs>
    </div>
  );
}
